import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Box,
  Chip,
  LinearProgress,
} from "@mui/material";
import { api } from "../api";
import AnalysisResult from "../components/AnalysisResult";
import EvidenceHistory from "../components/EvidenceHistory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DocumentViewerModal from "../components/DocumentViewerModal";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

function ObligationDetailPage() {
  const { id } = useParams();

  const navigate = useNavigate();

  const [obligation, setObligation] = useState(null);

  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);

  const [analysis, setAnalysis] = useState(null);

  const [history, setHistory] = useState([]);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState(null);

  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    loadData();
    loadHistory();
  }, [id]);

  const loadHistory = async () => {
    try {
      const response = await api.get(`/evidence/history/${id}`);

      setHistory(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const viewReport = async (uploadId) => {
    const response = await api.get(`/evidence/report/${uploadId}`);

    setSelectedReport(response.data);

    setModalOpen(true);
  };

  const downloadReport = async (uploadId) => {
    const response = await api.get(`/evidence/report/${uploadId}`);

    const blob = new Blob([JSON.stringify(response.data, null, 2)], {
      type: "application/json",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");

    a.href = url;

    a.download = `report-${uploadId}.json`;

    a.click();
  };

  const loadData = async () => {
    try {
      const response = await api.get(`/obligations/${id}`);

      setObligation(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    setError("");
    setSuccess("");

    // File type validation
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError("Only PDF, JPG, JPEG, PNG and WEBP files are allowed.");

      e.target.value = "";
      setFile(null);
      return;
    }

    // File size validation
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("File size must be less than 5 MB.");

      e.target.value = "";
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  const uploadEvidence = async () => {
    if (!file) {
      setError("Please select a file.");
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Only PDF, JPG, JPEG, PNG and WEBP files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be less than 5 MB.");
      return;
    }

    const formData = new FormData();

    formData.append("obligationId", id);

    formData.append("file", file);

    setAnalysis(null);
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      setError("");

      const response = await api.post("/evidence/validate", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },

        onUploadProgress: (event) => {
          const percentage = Math.round((event.loaded / event.total) * 100);

          setProgress(percentage);
        },
      });

      setAnalysis(response.data);

      await loadHistory();
      setSuccess("Evidence analyzed successfully.");
    } catch (err) {
      if (err.response) {
        setError(err.response.data.error || "Failed to analyze document");
      } else {
        setError("Unable to connect to server");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!obligation) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading obligation...</Typography>
      </Container>
    );
  }

  return (
    <Box mb={3}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1600px",
          mx: "auto",
          px: 4,
          mb: 4,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{
            borderRadius: 3,
            mb: 2,
            mt: 2,
            bgcolor: "#61ab29",
            color: "#fff",

            "&:hover": {
              bgcolor: "#EA580C",
            },
          }}
          onClick={() => navigate("/")}
        >
          Back to Dashboard
        </Button>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            border: "1px solid #e5e7eb",
          }}
        >
          <Chip
            label={obligation.status || "OPEN"}
            color="warning"
            sx={{ mb: 2 }}
          />
          <Typography variant="h4">{obligation.title}</Typography>

          <Typography mt={2}>Department: {obligation.department}</Typography>

          <Typography>Due Date: {obligation.due_date}</Typography>

          <Typography mt={2}>{obligation.description}</Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            mt: 3,
          }}
        >
          <Typography variant="h5">Upload Evidence</Typography>

          <Box
            sx={{
              border: "2px dashed #d1d5db",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              mt: 2,
            }}
          >
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={handleFileChange}
            />

            <Typography
              sx={{
                mt: 2,
                color: "#6b7280",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              {file
                ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
                : "Choose PDF or Image"}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                color: "#9ca3af",
              }}
            >
              Allowed: PDF, JPG, JPEG, PNG, WEBP • Maximum size: 5 MB
            </Typography>
          </Box>

          <br />
          {loading && (
            <>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ mt: 2 }}
              />

              <Typography sx={{ mt: 2, color: "#6b7280" }}>
                Uploading...
                {progress}%
              </Typography>
            </>
          )}
          {analysis?.aiDecision && (
            <Chip
              label={analysis.aiDecision.decision}
              sx={{
                bgcolor:
                  analysis.aiDecision.decision === "AUTO_APPROVE"
                    ? "#DCFCE7"
                    : analysis.aiDecision.decision === "REVIEW"
                      ? "#FFEDD5"
                      : "#FEE2E2",

                color:
                  analysis.aiDecision.decision === "AUTO_APPROVE"
                    ? "#15803D"
                    : analysis.aiDecision.decision === "REVIEW"
                      ? "#EA580C"
                      : "#DC2626",

                fontWeight: 700,
                display: "flex",
                alighnContent: "center",
                borderRadius: 3,
              }}
            />
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={uploadEvidence}
            disabled={loading || !file}
            sx={{
              mt: 2,
              color: "#fff",
              bgcolor: "#2563eb",
              "&:hover": {
                bgcolor: "#1d4ed8",
              },
            }}
          >
            {loading ? "Analyzing..." : "Analyze Evidence"}
          </Button>
        </Paper>

        {analysis && <AnalysisResult analysis={analysis} />}

        <EvidenceHistory
          history={history}
          onDownload={downloadReport}
          onView={viewReport}
        />
      </Container>
      <DocumentViewerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        report={selectedReport}
      />
    </Box>
  );
}

export default ObligationDetailPage;
